import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

// Utils
import { useTranslation } from 'next-i18next';
import { useMediaQuery } from 'react-responsive';
import { useRouter } from 'next/router';
import packageUtils from '../../../src/utils/package/packageUtils';
import { useAppContext } from '../../../src/store/AppContext';
import getLanguageId from '../../../src/utils/i18n/getLanguageId';
import cn from '../../../src/utils/cn';
import displayPrice from '../../../src/utils/displayPrice';
import usePrice from '../../../src/hooks/usePrice';

// Components
import Flag from '../../atoms/Flag/Flag';
import Button from '../../atoms/Button/Button';
import PreviousButton from '../../atoms/PreviousButton/PreviousButton';
import PeriodsList from '../../organisms/Cart/PlayerEntry/PeriodsList/PeriodsList';
import NextButton from '../../organisms/Cart/NextButton/NextButton';

import styles from './StickyTopBar.module.scss';

function StickyTopBar({
  pack, handleAddCamp, showTopBar, types, formats,
}) {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const isTablet = useMediaQuery({ maxWidth: 1024 });
  const isSmallMobile = useMediaQuery({ maxWidth: 340 });
  const [{ currentPlayer }] = useAppContext();

  const { getTotalPlayerPrice } = usePrice();

  const hasPeriodSelected = useMemo(() => (
    currentPlayer?.stage?.weeks?.length && currentPlayer?.stage?.packageId === pack.id
  ), [currentPlayer]);

  const lowestOfferPrice = useMemo(() => (
    packageUtils.getLowestOfferPrice(pack.offers?.length ? pack.offers : pack.otherOffers)
  ), [pack]);

  return (
    <section
      className={cn([
        styles.wrapper,
        'flex a-center j-spb',
        showTopBar ? styles.show : '',
      ])}
    >
      {!isTablet ? (
        <PreviousButton>{t('common.back')}</PreviousButton>
      ) : null}
      <div className="flex a-center j-end tablet-j-spb tablet-w-100">
        {!isTablet ? (
          <>
            <p className="fs-20 fw-700 text-c-primary lh-1-2">{pack.name[getLanguageId(locale)]}</p>
            <div className="flex a-center p-r-20 p-l-20">
              {types && Object.values(types)?.length ? (
                <Flag className="m-r-10">
                  {types[pack.typeId]?.name[getLanguageId(locale)]}
                </Flag>
              ) : null}
              {formats && Object.values(formats)?.length ? (
                <Flag>
                  {formats[pack.formatId]?.name[getLanguageId(locale)]}
                </Flag>
              ) : null }
            </div>
          </>
        ) : null}

        <div className={cn([hasPeriodSelected ? 'tablet-m-r-20' : ''])}>
          {pack.offers?.length || pack.otherOffers?.length ? (
            isTablet ? (
              hasPeriodSelected ? (
                <p className={`${styles.price} fs-20 fw-700 flex a-center`}>
                  {currentPlayer && getTotalPlayerPrice(currentPlayer) ? (
                    `${displayPrice(getTotalPlayerPrice(currentPlayer))} €`
                  ) : null}
                </p>
              ) : null
            ) : (
              <p className={`${styles.price} fs-20 fw-500`}>
                {`${t('package.priceFrom')} ${lowestOfferPrice} €`}
              </p>
            )
          ) : null}

          {isTablet && !isSmallMobile && hasPeriodSelected ? (
            <div className="m-t-5">
              <PeriodsList
                offersIds={currentPlayer.stage.weeks}
                durationId={currentPlayer.stage.durationId}
                days={currentPlayer.stage.durationInDays}
                listLength={1}
                className={styles.periods}
                canWrap
              />
            </div>
          ) : null}
        </div>

        <div className={cn(['m-l-20 tablet-m-l-0', !hasPeriodSelected && isTablet ? 'w-100' : ''])}>
          {hasPeriodSelected ? (
            isTablet ? (
              <NextButton
                isOutsideTunnel
                className={isTablet ? styles.tablet_cta_btn : ''}
              />
            ) : (
              <PeriodsList
                offersIds={currentPlayer.stage.weeks}
                durationId={currentPlayer.stage.durationId}
                days={currentPlayer.stage.durationInDays}
                listLength={1}
                className={styles.periods}
              />
            )
          ) : (
            <div className="flex a-center j-spb">
              {isTablet ? (
                <p className="fs-16 fw-400">
                  {t('package.priceFrom')}
                  <br />
                  <span className="fs-20 fw-700">
                    {`${lowestOfferPrice} €`}
                  </span>
                </p>
              ) : null}
              <Button
                text="uppercase"
                size={isTablet ? null : 'small'}
                onClick={handleAddCamp}
                classNames={isTablet ? styles.tablet_cta_btn : ''}
              >
                {t('common.add')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

StickyTopBar.propTypes = {
  pack: PropTypes.shape({}).isRequired,
  handleAddCamp: PropTypes.func,
  showTopBar: PropTypes.bool,
  formats: PropTypes.shape({}),
  types: PropTypes.shape({}),
};

StickyTopBar.defaultProps = {
  handleAddCamp: () => {},
  showTopBar: false,
  formats: null,
  types: null,
};

export default StickyTopBar;
